import { LockStatus, OSType } from '@electricjs/core_entity-client';

export enum DeviceIssueType {
  Encryption = 'encryption',
  Firewall = 'firewall',
  Gatekeeper = 'gatekeeper',
  OS = 'os',
  Storage = 'storage',
  RAM = 'ram',
  /* TODO HY-218 We're temporarily disabling Battery health while we improve the logic behind it */
  // BatteryHealth = 'battery_health',
  OsqueryNotReporting = 'osquery_not_reporting',
  MdmNotReporting = 'mdm_not_reporting',
  DeviceLock = 'device_lock',
}

export type DeviceIssueTypeOld = Exclude<
  DeviceIssueType,
  | typeof DeviceIssueType.OsqueryNotReporting
  | typeof DeviceIssueType.MdmNotReporting
  | typeof DeviceIssueType.DeviceLock
>;

export type IssueExtra = {
  eolDate: string;
  link: string;
};

export type IssueDetails = {
  category: string;
  actual: string;
  expected: string;
  extra?: IssueExtra | null;
};

export type Issue = {
  [DeviceIssueType.Encryption]?: IssueDetails;
  [DeviceIssueType.Firewall]?: IssueDetails;
  [DeviceIssueType.Gatekeeper]?: IssueDetails;
  [DeviceIssueType.OS]?: IssueDetails;
  [DeviceIssueType.Storage]?: IssueDetails;
};

export type UpdateDeviceArgs = {
  organizationId: string;
  deviceId: string;
  lockStatus?: string;
  lockPin?: string;
  mdmEnrolledAt?: string;
  electricAppInstalledAt?: string;
  assignedTo?: string | null;
  employeeId: string;
};

export type Device = {
  serialNumber: string;
  hardwareModel: string;
  osType: string;
  organizationId: string;
  id: string;
  lockStatus?: LockStatus;
  lockPin?: string;
  mdmEnrolledAt?: string;
  assetId?: string;
  electricAppInstalledAt?: string;
};

export type DeviceReportingDevice = {
  id?: string;
  organization_id?: string;
  serial_number?: string;
  hardware_model?: string;
  hostname?: string;
  os_type?: OSType;
  os_version?: string;
  os_name?: string;
  ram_capacity?: number;
  storage_capacity?: number;
  storage_available?: number;
  storage_usage_percent?: number;
  filevault?: boolean;
  filevault_status?: string;
  filevault_encryption_percent?: number;
  bitlocker?: boolean;
  bitlocker_status?: string;
  bitlocker_encryption_percent?: number;
  encryption?: boolean;
  encryption_percent?: number;
  battery_health?: string;
  firewall?: boolean;
  gatekeeper?: boolean;
  issues?: Issue;
  mdm_enrolled_at?: string;
  created_at?: string;
  updated_at?: string;
  lock_status?: LockStatus;
  lock_pin?: string;
  asset_id?: string;
  last_osquery_enrollment?: string;
  last_osquery_report?: string;
};

export type ActiveEmployees = {
  total: number;
  withDevices: number;
  withoutDevices: number;
};

export type InactiveEmployees = {
  total: number;
};

export type UnknownEmployees = {
  total: number;
};

export type EmployeesMetrics = {
  total: number;
  active: ActiveEmployees;
  inactive: InactiveEmployees;
  unknown: UnknownEmployees;
};

export type MdmEnrollmentMetrics = {
  mdmEnrolled: number;
  mdmUnenrolled: number;
  total: number;
};

export type ActiveDevicesMetrics = {
  total: number;
  assignedToActiveEmployees: MdmEnrollmentMetrics;
  assignedToInactiveEmployees: MdmEnrollmentMetrics;
  unassigned: MdmEnrollmentMetrics;
};

export type InactiveDevicesMetrics = {
  archived: number;
  donated: number;
  sold: number;
  disposed: number;
  missing: number;
};

export type DevicesMetrics = {
  total: number;
  active: ActiveDevicesMetrics;
  inactive: InactiveDevicesMetrics;
};

export type DevicesMetricsResponse = {
  employees: EmployeesMetrics;
  devices: DevicesMetrics;
};

export type OsVersionDetail = {
  osName: string;
  osVersion: string;
  count: number;
};

export type OsTypeEnrollmentBreakdown = {
  total: number;
  versions: number;
  atEol: number;
  nearEol: number;
  supported: number;
  unknown: number;
  versionDetails: OsVersionDetail[];
};

export type EnrollmentBreakdown = {
  darwin: OsTypeEnrollmentBreakdown;
  windows: OsTypeEnrollmentBreakdown;
};

export type MdmDevicesMetrics = {
  managedDevices: number;
  unmanagedDevices: number;
  unmanagedDevicesIds: string[];
  assignedEnrolled: EnrollmentBreakdown;
  unassignedEnrolled: EnrollmentBreakdown;
};

export type MdmDevicesMetricsResponse = {
  devices: MdmDevicesMetrics;
  employeesWithoutAssets: number;
};

export type GetDeviceMetricsArgs = {
  organizationId: string;
};

export type EmployeeDeviceOsGrouping = {
  employee_id: string;
  os_type: string;
  device_id: string;
};

export type IssueEmailArgs = {
  employeeDeviceOsGrouping: EmployeeDeviceOsGrouping[];
  issueType: string;
  organizationId: string;
};

export type CreateDeviceArgs = {
  organizationId: string;
  employeeId: string;
  serialNumber: string;
  hardwareModel: string;
  osType: 'darwin' | 'windows';
  assignedTo?: string;
  electricAppInstalledAt?: string;
};

export type DeleteDeviceArgs = {
  organizationId: string;
  deviceId: string;
  employeeId: string;
};

export type DeleteTestDeviceArgs = {
  organizationId: string;
  deviceId: string;
  employeeId: string;
};

export type CreateFakeDeviceArgs = {
  organizationId: string;
  employeeId: string;
  deviceId: string;
  serialNumber: string;
  hardwareModel: string;
  osType: 'darwin' | 'windows';
  issues: string[];
};

export type DeleteDeviceReportingArgs = {
  organizationId: string;
  deviceId: string;
};

export type EndOfLifeDto = {
  product: string;
  version: string;
  eol_date?: string;
  link?: string;
  latest?: string;
  codename?: string;
  release_label?: string;
  release_date?: string;
  latest_release_date?: string;
  lts?: boolean;
  support?: string;
  extended_support?: string;
};

export type EndOfLife = {
  product: string;
  version: string;
  eolDate?: string;
  link?: string;
  latest?: string;
  codename?: string;
  releaseLabel?: string;
  releaseDate?: string;
  latestReleaseDate?: string;
  lts?: boolean;
  support?: string;
  extendedSupport?: string;
};

export type EndOfLifeListResponse<T> = {
  total: number;
  results: T[];
};

export const EOLStatusEnum = {
  UNKNOWN: 'unknown',
  SUPPORTED: 'supported',
  WARNING: 'warning',
  DANGER: 'danger',
} as const;

export type EOLStatus = (typeof EOLStatusEnum)[keyof typeof EOLStatusEnum];
